import React, { useState } from 'react';
import { Lock, Plus, Trash, Bell, Globe, Smartphone } from 'lucide-react';
import AdminPageShell from "../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const SectionTitle = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2">
    <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
    {Icon ? <Icon size={18} className="text-slate-700" /> : null}
    <h2 className="text-sm md:text-base font-black tracking-tight text-slate-900">{title}</h2>
  </div>
);

const FieldBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors',
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors',
      className
    )}
  />
);

const SecuritySettings = () => {
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
  const [passwordExpiry, setPasswordExpiry] = useState(90);
  const [ipWhitelist, setIpWhitelist] = useState(['192.168.1.1', '10.0.0.1']);
  const [newIp, setNewIp] = useState('');
  const [securityAlerts, setSecurityAlerts] = useState({
    failedLogin: true,
    newDevice: true,
    passwordChange: true,
    adminAction: true,
  });

  const handleAddIp = () => {
    if (newIp.trim() && !ipWhitelist.includes(newIp.trim())) {
      setIpWhitelist([...ipWhitelist, newIp.trim()]);
      setNewIp('');
    }
  };

  const handleRemoveIp = (ip) => {
    setIpWhitelist(ipWhitelist.filter(ipAddr => ipAddr !== ip));
  };

  const mockTrustedDevices = [
    { id: 1, name: 'Chrome on Windows', ip: '192.168.1.1', lastUsed: '2024-06-01', location: 'New York, US' },
    { id: 2, name: 'Safari on iPhone', ip: '10.0.0.1', lastUsed: '2024-05-30', location: 'New York, US' },
    { id: 3, name: 'Firefox on Mac', ip: '172.16.0.1', lastUsed: '2024-05-25', location: 'San Francisco, US' },
  ];

  const mockSecurityLogs = [
    { id: 1, action: 'Failed Login', user: 'admin@example.com', ip: '203.0.113.1', timestamp: '2024-06-01 14:30', severity: 'high' },
    { id: 2, action: 'Password Changed', user: 'john@admin.com', ip: '192.168.1.1', timestamp: '2024-06-01 12:15', severity: 'medium' },
    { id: 3, action: 'New Device Login', user: 'jane@admin.com', ip: '10.0.0.1', timestamp: '2024-06-01 10:45', severity: 'low' },
  ];

  return (
    <AdminPageShell
      title="Security"
      description="Security controls for admin access, devices, and alerting. (Mock settings)"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Two-Factor Authentication */}
        <Card className="p-5">
          <SectionTitle icon={Smartphone} title="Two-Factor Authentication" />
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-slate-900">Enable 2FA</p>
                <p className="text-sm text-slate-600">Require authentication code for login</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={twoFactorEnabled}
                  onChange={(e) => setTwoFactorEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#D2042D]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
              </label>
            </div>
            {twoFactorEnabled && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm text-slate-700">
                  <strong>Status:</strong> 2FA is enabled for all admin accounts
                </p>
              </div>
            )}
            <PrimaryButton className="w-full justify-center">Configure 2FA Settings</PrimaryButton>
          </div>
        </Card>

        {/* Password Policy */}
        <Card className="p-5">
          <SectionTitle icon={Lock} title="Password Policy" />
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Password Expiry (days)
              </label>
              <input
                type="number"
                value={passwordExpiry}
                onChange={(e) => setPasswordExpiry(e.target.value)}
                className={FieldBase}
                min="0"
                max="365"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Require uppercase letters</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Require lowercase letters</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Require numbers</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Require special characters</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Minimum 8 characters</span>
              </label>
            </div>
          </div>
        </Card>

        {/* IP Whitelist */}
        <Card className="p-5">
          <SectionTitle icon={Globe} title="IP Whitelist" />
          <div className="mt-4 space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={newIp}
                onChange={(e) => setNewIp(e.target.value)}
                placeholder="Enter IP address"
                className={FieldBase}
              />
              <PrimaryButton
                onClick={handleAddIp}
              >
                <Plus /> Add
              </PrimaryButton>
            </div>
            <div className="space-y-2">
              {ipWhitelist.map((ip, index) => (
                <div key={index} className="flex items-center justify-between bg-slate-50 p-3 rounded-2xl border border-slate-200">
                  <span className="text-sm font-mono">{ip}</span>
                  <button
                    onClick={() => handleRemoveIp(ip)}
                    className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                  >
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Security Alerts */}
        <Card className="p-5">
          <SectionTitle icon={Bell} title="Security Alerts" />
          <div className="mt-4 space-y-4">
            {Object.entries(securityAlerts).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-900 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</p>
                  <p className="text-sm text-slate-600">Get notified when this event occurs</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setSecurityAlerts({ ...securityAlerts, [key]: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#D2042D]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                </label>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Trusted Devices */}
      <Card className="mt-6 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <SectionTitle title="Trusted Devices" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Device</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Last Used</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {mockTrustedDevices.map((device) => (
                <tr key={device.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-slate-50 rounded-full border border-slate-200 flex items-center justify-center">
                        <Smartphone className="text-slate-700" size={14} />
                      </div>
                      <span className="ml-3 text-sm font-semibold text-slate-900">{device.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 font-mono">{device.ip}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{device.location}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{device.lastUsed}</td>
                  <td className="px-4 py-3">
                    <SecondaryButton className="px-3 py-1.5 text-xs">
                      Remove
                    </SecondaryButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Security Logs */}
      <Card className="mt-6 overflow-hidden">
        <div className="p-5 border-b border-slate-200">
          <SectionTitle title="Recent Security Events" />
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Event</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">IP Address</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Timestamp</th>
                <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Severity</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {mockSecurityLogs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-slate-900">{log.action}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{log.user}</td>
                  <td className="px-4 py-3 text-sm text-slate-600 font-mono">{log.ip}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{log.timestamp}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold capitalize ${
                      log.severity === 'high' ? 'border-rose-200 bg-rose-50 text-rose-800' :
                      log.severity === 'medium' ? 'border-amber-200 bg-amber-50 text-amber-800' :
                      'border-emerald-200 bg-emerald-50 text-emerald-800'
                    }`}>
                      {log.severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </AdminPageShell>
  );
};

export default SecuritySettings; 